export const PAID_CLICK_COOKIE_NAME = "paid_click"

// 30 days, matching the existing broker cookies
const COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60

const MAX_VALUE_LENGTH = 100

export const PAID_CLICK_BROKERS = ["google", "meta"] as const

export type PaidClickBroker = (typeof PAID_CLICK_BROKERS)[number]

export interface PaidClick {
  broker: PaidClickBroker
  campaign?: string
  campaignId?: string
}

interface PaidClickRule {
  broker: PaidClickBroker
  clickIdParams: readonly string[]
  utmSources: readonly string[]
  utmMediums: readonly string[]
  campaignIdParam?: string
}

const PAID_CLICK_RULES: readonly PaidClickRule[] = [
  {
    broker: "google",
    clickIdParams: ["gclid", "gbraid", "wbraid"],
    utmSources: ["google"],
    utmMediums: ["cpc"],
    campaignIdParam: "gad_campaignid",
  },
  {
    broker: "meta",
    clickIdParams: [],
    utmSources: ["facebook", "fb", "instagram", "ig", "meta"],
    utmMediums: ["paid", "paid_social", "paidsocial", "cpc"],
  },
]

const param = (params: URLSearchParams, name: string) =>
  params.get(name)?.trim().slice(0, MAX_VALUE_LENGTH) || undefined

function matchesRule(params: URLSearchParams, rule: PaidClickRule): boolean {
  if (rule.clickIdParams.some((name) => param(params, name))) return true

  const source = param(params, "utm_source")?.toLowerCase()
  const medium = param(params, "utm_medium")?.toLowerCase()

  return (
    !!source &&
    !!medium &&
    rule.utmSources.includes(source) &&
    rule.utmMediums.includes(medium)
  )
}

export function detectPaidClick(
  params: URLSearchParams,
): PaidClick | undefined {
  const rule = PAID_CLICK_RULES.find((rule) => matchesRule(params, rule))
  if (!rule) return undefined

  return {
    broker: rule.broker,
    campaign: param(params, "utm_campaign"),
    campaignId: rule.campaignIdParam
      ? param(params, rule.campaignIdParam)
      : undefined,
  }
}

export function appendPaidClickCookie(
  params: URLSearchParams,
  headers: Headers,
): void {
  const paidClick = detectPaidClick(params)
  if (!paidClick) return

  const value = encodeURIComponent(JSON.stringify(paidClick))
  headers.append(
    "Set-Cookie",
    `${PAID_CLICK_COOKIE_NAME}=${value}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax; Secure`,
  )
}
